import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPackage, FiMapPin, FiEye, FiHash, FiClock, FiTruck, FiShield, FiCamera, FiFileText, FiBarChart, FiGrid, FiRefreshCw, FiFilter } from 'react-icons/fi';
import LotDetailModal from './LotDetailModal';
import TrackingMap from './TrackingMap';

const TrackingContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a3e 0%, #2d1b69 100%);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.8rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #a0a0a0;
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ControlsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SearchControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #fff;
  font-size: 1rem;
  min-width: 300px;
  
  &:focus {
    border-color: #4facfe;
    box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #a0a0a0;
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(79, 172, 254, 0.1);
    border-color: #4facfe;
  }
`;

const StatsOverview = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  
  .stat-icon {
    font-size: 2rem;
    color: #4facfe;
    margin-bottom: 1rem;
  }
  
  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 0.5rem;
  }
  
  .stat-label {
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const LotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const LotCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-5px);
    border-color: #4facfe;
    box-shadow: 0 10px 30px rgba(79, 172, 254, 0.2);
  }
`;

const LotHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const LotId = styled.div`
  .lot-number {
    font-size: 1.3rem;
    font-weight: 700;
    color: #4facfe;
    margin-bottom: 0.3rem;
  }
  
  .lot-type {
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const QRCodeButton = styled.button`
  background: rgba(79, 172, 254, 0.1);
  border: 1px solid #4facfe;
  border-radius: 8px;
  padding: 0.5rem;
  color: #4facfe;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(79, 172, 254, 0.2);
  }
`;

const LotDetails = styled.div`
  margin-bottom: 1rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  
  .label {
    color: #a0a0a0;
    font-size: 0.9rem;
  }
  
  .value {
    color: #fff;
    font-weight: 600;
  }
`;

const BlockchainHash = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 1rem;
  
  .hash-label {
    color: #00ff88;
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }
  
  .hash-value {
    font-family: monospace;
    font-size: 0.8rem;
    color: #fff;
    word-break: break-all;
    background: rgba(0, 0, 0, 0.3);
    padding: 0.5rem;
    border-radius: 5px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  flex: 1;
  min-width: 120px;
  padding: 0.8rem;
  background: ${props => props.primary ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(79, 172, 254, 0.3);
  }
`;

const RealtimeEvents = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
`;

const EventsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h2 {
    color: #fff;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const EventsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const EventItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  margin-bottom: 1rem;
  border-left: 3px solid ${props => {
    switch(props.type) {
      case 'deposit': return '#00ff88';
      case 'withdrawal': return '#ff4757';
      case 'movement': return '#ffc107';
      case 'inspection': return '#4facfe';
      default: return '#a0a0a0';
    }
  }};
  
  .event-icon {
    font-size: 1.5rem;
    color: ${props => {
      switch(props.type) {
        case 'deposit': return '#00ff88';
        case 'withdrawal': return '#ff4757';
        case 'movement': return '#ffc107';
        case 'inspection': return '#4facfe';
        default: return '#a0a0a0';
      }
    }};
  }
  
  .event-details {
    flex: 1;
    
    .event-title {
      color: #fff;
      font-weight: 600;
      margin-bottom: 0.3rem;
    }
    
    .event-description {
      color: #a0a0a0;
      font-size: 0.9rem;
      margin-bottom: 0.3rem;
    }
    
    .event-hash {
      font-family: monospace;
      font-size: 0.8rem;
      color: #4facfe;
    }
  }
  
  .event-time {
    color: #a0a0a0;
    font-size: 0.8rem;
    white-space: nowrap;
  }
`;

function AssetTracking() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLot, setSelectedLot] = useState(null);
  const [realtimeEvents, setRealtimeEvents] = useState([]);
  const [sugarLots, setSugarLots] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Mock data for sugar lots
  useEffect(() => {
    const mockLots = [
      {
        id: 'SUG-001-2024',
        type: 'Raw Sugar',
        icumsa: 30,
        quantity: 25000,
        origin: 'Brazil',
        warehouse: 'Dubai DMCC Warehouse A',
        temperature: 22.5,
        moisture: 0.03,
        purity: 99.7,
        lastInspection: '2024-01-15',
        blockchainAddress: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
        images: 4,
        assayReports: 2,
        status: 'Active'
      },
      {
        id: 'SUG-002-2024',
        type: 'White Refined Sugar',
        icumsa: 45,
        quantity: 18500,
        origin: 'Thailand',
        warehouse: 'Singapore Port Storage',
        temperature: 23.1,
        moisture: 0.02,
        purity: 99.9,
        lastInspection: '2024-01-14',
        blockchainAddress: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
        images: 6,
        assayReports: 3,
        status: 'Active'
      },
      {
        id: 'SUG-003-2024',
        type: 'Organic Sugar',
        icumsa: 100,
        quantity: 12000,
        origin: 'India',
        warehouse: 'Swiss Commodity Vault',
        temperature: 21.8,
        moisture: 0.04,
        purity: 99.5,
        lastInspection: '2024-01-13',
        blockchainAddress: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
        images: 3,
        assayReports: 2,
        status: 'Active'
      }
    ];
    setSugarLots(mockLots);
  }, []);

  // Simulate real-time events
  useEffect(() => {
    const events = [
      {
        id: 1,
        type: 'deposit',
        title: 'New Sugar Deposit',
        description: 'SUG-004-2024: 30,000 kg deposited at Dubai DMCC',
        hash: '0x4d5e6f7890abcdef...',
        timestamp: new Date().toISOString(),
        icon: FiPackage
      },
      {
        id: 2,
        type: 'inspection',
        title: 'Quality Inspection',
        description: 'SUG-001-2024: Third-party inspection completed',
        hash: '0x5e6f7890abcdef12...',
        timestamp: new Date(Date.now() - 300000).toISOString(),
        icon: FiShield
      },
      {
        id: 3,
        type: 'movement',
        title: 'Warehouse Transfer',
        description: 'SUG-002-2024: Moved to Section B for better climate control',
        hash: '0x6f7890abcdef1234...',
        timestamp: new Date(Date.now() - 600000).toISOString(),
        icon: FiTruck
      }
    ];
    
    setRealtimeEvents(events);

    // Simulate new events every 30 seconds
    const interval = setInterval(() => {
      const newEvent = {
        id: Date.now(),
        type: ['deposit', 'withdrawal', 'movement', 'inspection'][Math.floor(Math.random() * 4)],
        title: 'Real-time Event',
        description: `SUG-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}-2024: Automated blockchain update`,
        hash: `0x${Math.random().toString(16).substring(2, 18)}...`,
        timestamp: new Date().toISOString(),
        icon: FiHash
      };
      
      setRealtimeEvents(prev => [newEvent, ...prev.slice(0, 9)]);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const filteredLots = sugarLots.filter(lot => 
    lot.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lot.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lot.origin.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getEventIcon = (type) => {
    switch(type) {
      case 'deposit': return FiPackage;
      case 'withdrawal': return FiTruck;
      case 'movement': return FiRefreshCw;
      case 'inspection': return FiShield;
      default: return FiHash;
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const handleViewDetails = (lot) => {
    setSelectedLot(lot);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedLot(null);
  };

  return (
    <TrackingContainer>
      <Header>
        <Title>Asset Tracking System</Title>
        <Subtitle>
          Real-time blockchain-verified tracking of physical sugar lots with tamper-evident records, 
          complete audit trails, and comprehensive asset documentation
        </Subtitle>
      </Header>

      <ControlsSection>
        <SearchControls>
          <SearchInput
            type="text"
            placeholder="Search by Lot ID, Type, or Origin..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FilterButton>
            <FiFilter />
            Filter
          </FilterButton>
        </SearchControls>
        
        <FilterButton>
          <FiRefreshCw />
          Refresh Data
        </FilterButton>
      </ControlsSection>

      <StatsOverview>
        <StatCard>
          <FiPackage className="stat-icon" />
          <div className="stat-value">{sugarLots.length}</div>
          <div className="stat-label">Active Lots</div>
        </StatCard>
        <StatCard>
          <FiBarChart className="stat-icon" />
          <div className="stat-value">{sugarLots.reduce((sum, lot) => sum + lot.quantity, 0).toLocaleString()}</div>
          <div className="stat-label">Total Kg Tracked</div>
        </StatCard>
        <StatCard>
          <FiMapPin className="stat-icon" />
          <div className="stat-value">3</div>
          <div className="stat-label">Global Warehouses</div>
        </StatCard>
        <StatCard>
          <FiHash className="stat-icon" />
          <div className="stat-value">{realtimeEvents.length}</div>
          <div className="stat-label">Blockchain Events</div>
        </StatCard>
      </StatsOverview>

      {/* Professional Tracking Map */}
      <TrackingMap />

      <LotsGrid>
        {filteredLots.map(lot => (
          <LotCard key={lot.id}>
            <LotHeader>
              <LotId>
                <div className="lot-number">{lot.id}</div>
                <div className="lot-type">{lot.type} • ICUMSA {lot.icumsa}</div>
              </LotId>
              <QRCodeButton>
                <FiGrid />
              </QRCodeButton>
            </LotHeader>

            <LotDetails>
              <DetailRow>
                <span className="label">Quantity:</span>
                <span className="value">{lot.quantity.toLocaleString()} kg</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Origin:</span>
                <span className="value">{lot.origin}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Warehouse:</span>
                <span className="value">{lot.warehouse}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Temperature:</span>
                <span className="value">{lot.temperature}°C</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Moisture:</span>
                <span className="value">{(lot.moisture * 100).toFixed(1)}%</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Purity:</span>
                <span className="value">{lot.purity}%</span>
              </DetailRow>
            </LotDetails>

            <BlockchainHash>
              <div className="hash-label">
                <FiHash />
                Blockchain Address (Tamper-Evident)
              </div>
              <div className="hash-value">{lot.blockchainAddress}</div>
            </BlockchainHash>

            <ActionButtons>
              <ActionButton primary onClick={() => handleViewDetails(lot)}>
                <FiEye />
                View Details
              </ActionButton>
              <ActionButton>
                <FiCamera />
                Images ({lot.images})
              </ActionButton>
              <ActionButton>
                <FiFileText />
                Assays ({lot.assayReports})
              </ActionButton>
            </ActionButtons>
          </LotCard>
        ))}
      </LotsGrid>

      <RealtimeEvents>
        <EventsHeader>
          <h2>
            <FiClock />
            Real-Time Inventory Events
          </h2>
          <FilterButton>
            <FiRefreshCw />
            Live Feed
          </FilterButton>
        </EventsHeader>

        <EventsList>
          {realtimeEvents.map(event => {
            const IconComponent = getEventIcon(event.type);
            return (
              <EventItem key={event.id} type={event.type}>
                <IconComponent className="event-icon" />
                <div className="event-details">
                  <div className="event-title">{event.title}</div>
                  <div className="event-description">{event.description}</div>
                  <div className="event-hash">Hash: {event.hash}</div>
                </div>
                <div className="event-time">{formatTime(event.timestamp)}</div>
              </EventItem>
            );
          })}
        </EventsList>
      </RealtimeEvents>

      {showDetailModal && (
        <LotDetailModal 
          lot={selectedLot} 
          onClose={closeDetailModal}
        />
      )}
    </TrackingContainer>
  );
}

export default AssetTracking; 